import React, { useState, useEffect, use } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Timer, Trophy, RefreshCw } from 'lucide-react';
import { useLanguage } from 'context/LanguageProvider';
import GameOverModal from 'components/GameOverModal';
import { useDarkMode } from 'context/DarkModeProvider';
import GameWonModal from 'components/GameWonModal';

const HardcoreHashChallenge = () => {
    const [hashTable, setHashTable] = useState(Array(7).fill(null).map(() => []));
    const [availableNodes, setAvailableNodes] = useState([]);
    const [draggedNode, setDraggedNode] = useState(null);
    const [score, setScore] = useState(0);
    const [message, setMessage] = useState('');
    const [gameState, setGameState] = useState('playing');
    const [timeLeft, setTimeLeft] = useState(180);
    const [gameStarted, setGameStarted] = useState(false);
    const [lives, setLives] = useState(3);
    const [gameOver, setGameOver] = useState(false);
    const [gameWon, setGameWon] = useState(false);
    const tableSize = 7;

    const { selLanguage, a, b } = useLanguage();
    const { darkMode } = useDarkMode();

    const hashFunction = (key) => {
        const numKey = typeof key === 'number'
            ? key
            : parseInt(key) || key.charCodeAt(0) || 0;
        return ((numKey * 11) + 13) % tableSize;
    };


    const generateNodes = () => {
        const nodes = [];
        const numbers = [23, 45, 67, 89, 12, 34, 56, 78];

        nodes.push(...numbers.slice(0, 8));

        return nodes.sort(() => Math.random() - 0.5);
    };

    useEffect(() => {
        initializeGame();
    }, []);

    useEffect(() => {
        if (gameStarted && gameState === 'playing' && timeLeft > 0) {
            const timer = setInterval(() => {
                setTimeLeft(prev => {
                    if (prev <= 1) {
                        setGameState('lost');
                        setMessage(' Ο χρόνος τελείωσε! Game Over!');
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);
            return () => clearInterval(timer);
        }
    }, [gameStarted, gameState, timeLeft]);

    const initializeGame = () => {
        setHashTable(Array(tableSize).fill(null).map(() => []));
        const nodes = generateNodes();
        setAvailableNodes(nodes);
        setMessage('');
        setGameWon(false);
        setGameState('playing');
        setGameOver(false);
        setTimeLeft(180);
        setScore(0);
        setLives(3);
        setGameStarted(true);
    };

    const handleDragStart = (e, node) => {
        if (gameState !== 'playing') return;
        setDraggedNode(node);
        e.dataTransfer.setData('text/plain', String(node));
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.currentTarget.style.background = 'rgba(255,255,255,0.15)';
    };

    const handleDragLeave = (e) => {
        e.currentTarget.style.background = 'rgba(255,255,255,0.05)';
    };

    const handleDrop = (index) => (e) => {
        e.preventDefault();
        e.currentTarget.style.background = 'rgba(255,255,255,0.05)';

        if (gameState !== 'playing') return;

        const nodeStr = e.dataTransfer.getData('text/plain');
        if (!nodeStr) return;

        const node = Number(nodeStr); // 👈 μετατροπή σε number

        const correctIndex = hashFunction(node);

        if (correctIndex !== index) {
            const newLives = lives - 1;
            setLives(newLives);
            setDraggedNode(null);

            if (newLives === 0) {
                setGameState('lost');
                setMessage(`Έχασες όλες τις ζωές σου! Ο κόμβος "${node}" έπρεπε να πάει στη θέση ${correctIndex}, όχι ${index}`);
                setGameOver(true);
            } else {
                setMessage(`Λάθος! Ο κόμβος "${node}" πρέπει να πάει στη θέση ${correctIndex}, όχι ${index}. Απομένουν ${newLives} ζωές!`);
            }
            return;
        }

        const newHashTable = [...hashTable];
        newHashTable[index].push(node);
        setHashTable(newHashTable);

        const newAvailableNodes = availableNodes.filter(n => n !== node); // τώρα δουλεύει, όλα number
        setAvailableNodes(newAvailableNodes);
        setDraggedNode(null);

        if (newAvailableNodes.length === 0) {
            setGameState('won');
            setGameWon(true);
            const timeBonus = Math.floor(timeLeft * 10);
            const totalScore = 8 * 100 + timeBonus;
            setScore(totalScore);
            setMessage(`ΤΕΛΕΙΑ! Όλοι οι κόμβοι στη σωστή θέση! Χρόνος: ${formatTime(timeLeft)} | Score: ${totalScore}`);
        } else {
            setMessage(`Σωστά! Συνεχίστε... (${newAvailableNodes.length} κόμβοι απομένουν)`);
        }
    };

    const removeFromTable = (tableIndex, nodeIndex) => {
        if (gameState !== 'playing') return;

        const node = hashTable[tableIndex][nodeIndex];
        const newHashTable = [...hashTable];
        newHashTable[tableIndex].splice(nodeIndex, 1);
        setHashTable(newHashTable);
        setAvailableNodes(prev => [...prev, node]);
        setMessage('Τοποθετήστε όλους τους κόμβους στη σωστή θέση');
    };

    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    const getTimerColor = () => {
        if (timeLeft > 120) return '#10b981';
        if (timeLeft > 60) return '#f59e0b';
        return '#ef4444';
    };

    return (
        <div
            className='main-content'
            style={{
                minHeight: "100vh",
                background: darkMode ? "linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #101724ff 100%)" : "var(--lightModeGameBackground)",
                padding: "30px 10px",
                borderRadius: "10px",
                boxSizing: "border-box",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "flex-start",
                overflowX: "hidden"
            }}>

            <GameOverModal
                isVisible={gameOver}
                onPlayAgain={initializeGame}
            />


            <GameWonModal
                isVisible={gameWon}
                onPlayAgain={initializeGame}
            />

            <div style={{ maxWidth: "1200px", margin: "0 auto" }}>
                {/* Header with Lives and Timer */}
                <div style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "20px",
                    flexWrap: "wrap",
                    gap: "15px"
                }}>


                    {/* Timer */}
                    <div style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "12px",
                        background: "rgba(0,0,0,0.3)",
                        padding: "12px 20px",
                        borderRadius: "15px",
                        border: `2px solid ${getTimerColor()}`
                    }}>
                        <Timer size={28} color={getTimerColor()} />
                        <span style={{
                            fontSize: "28px",
                            fontWeight: "bold",
                            color: getTimerColor(),
                            fontFamily: "monospace"
                        }}>
                            {formatTime(timeLeft)}
                        </span>
                    </div>
                    <div style={{
                        display: "flex",
                        alignItems: "center",
                        flexDirection: "row",
                        justifyContent: "right",
                        gap: "12px"
                    }}>

                        <div style={{
                            display: "flex",
                            gap: "6px",
                            alignItems: "center"
                        }}>
                            {[1, 2, 3].map((heartNum) => (
                                <div
                                    key={heartNum}
                                    style={{
                                        fontSize: "24px",
                                        filter: lives >= heartNum
                                            ? "drop-shadow(0 4px 8px rgba(220, 38, 38, 0.4))"
                                            : "grayscale(100%) opacity(0.3)",
                                        transform: lives >= heartNum ? "scale(1)" : "scale(0.8)",
                                        transition: "all 0.3s ease",
                                        animation: lives >= heartNum ? "heartbeat 2s ease-in-out infinite" : "none"
                                    }}
                                >
                                    ❤️
                                </div>
                            ))}
                        </div>
                    </div>
                </div>


                {/* Mission Statement */}
                <div style={{
                    borderRadius: "20px",
                    maxWidth: "770px",
                    background: "linear-gradient(135deg, rgba(59, 130, 246, 0.4), rgba(29, 78, 216, 0.4))",
                    width: "90%",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    padding: "15px 20px",
                    margin: "0 auto 20px",
                    color: "white",
                    textAlign: "center",
                    lineHeight: "1.6"
                }}>
                    <p style={{ margin: 0 }}>

                        {selLanguage ? "Έστω έχουμε έναν πίνακα M=7 και μια HashFunction : h(k) = (k × 7 + 13) mod 7. Τοποθετήστε όλους τους κόμβους στη σωστή θέση! Κάθε λάθος κοστίζει μία ζωή! Για να σύρεις έναν κόμβο προς τα πάνω ή κάτω πήγαινε τον κόμβο στην άκρη της οθόνης και μετά στο σωστό κουτί." : "Given a table M=7 and a HashFunction: h(k) = (k × 7 + 13) mod 7. Place all nodes in the correct position! Each mistake costs one life! To drag a node up or down, move the node to the edge of the screen and then to the correct box."}
                    </p>
                </div>

                {/* Hash Table */}
                <motion.div
                    layout
                    style={{
                        background: "linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)",
                        borderRadius: "12px",
                        padding: "20px",
                        marginBottom: "20px",
                        border: "1px solid rgba(255,255,255,0.2)"
                    }}
                >
                    <h3 style={{
                        color: "white",
                        textAlign: "center",
                        marginBottom: "16px",
                        fontSize: "22px"
                    }}>
                        Hash Table
                    </h3>
                    <div style={{ display: "grid", gap: "12px" }}>
                        {hashTable.map((chain, index) => (
                            <motion.div
                                key={index}
                                layout
                                onDragOver={handleDragOver}
                                onDragLeave={handleDragLeave}
                                onDrop={handleDrop(index)}
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    background: "rgba(255,255,255,0.05)",
                                    borderRadius: "12px",
                                    padding: "12px",
                                    border: "2px dashed rgba(255,255,255,0.3)",
                                    minHeight: "100px",
                                    transition: "all 0.2s ease"
                                }}
                            >
                                <div style={{
                                    width: "64px",
                                    height: "64px",
                                    background: "linear-gradient(135deg, #1655bb 0%, #042277 100%)",
                                    borderRadius: "16px",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    color: "white",
                                    fontWeight: "bold",
                                    fontSize: "20px",
                                    marginRight: "16px",
                                    flexShrink: 0
                                }}>
                                    {index}
                                </div>

                                <div style={{
                                    flex: 1,
                                    minHeight: "60px",
                                    display: "flex",
                                    alignItems: "center",
                                    flexWrap: "wrap",
                                    gap: "8px"
                                }}>
                                    {chain.length === 0 ? (
                                        <div style={{
                                            color: "#e5e6e7",
                                            fontStyle: "italic",
                                            textAlign: "center",
                                            width: "100%"
                                        }}>
                                            {selLanguage ? "Σύρετε κόμβους εδώ..." : "Drag nodes here..."}
                                        </div>
                                    ) : (
                                        chain.map((node, nodeIndex) => (
                                            <motion.div
                                                key={`${node}-${nodeIndex}`}
                                                initial={{ opacity: 0, scale: 0.8, y: -10 }}
                                                animate={{ opacity: 1, scale: 1, y: 0 }}
                                                transition={{ duration: 0.3 }}
                                                onClick={() => removeFromTable(index, nodeIndex)}
                                                style={{
                                                    padding: "8px 16px",
                                                    borderRadius: "12px",
                                                    color: "rgba(8, 56, 134, 1)",
                                                    background: '#e2e2e2',
                                                    fontWeight: "bold",
                                                    cursor: "pointer",
                                                    boxShadow: "0 4px 8px rgba(0,0,0,0.15)",
                                                    transition: "transform 0.2s ease",
                                                    border: "2px solid #2f31a3"
                                                }}
                                                whileHover={{ scale: 1.05 }}
                                            >
                                                {node}
                                            </motion.div>
                                        ))
                                    )}
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </motion.div>

                {message && <AnimatePresence>
                    <motion.div
                        key={message}
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.9 }}
                        style={{
                            background: message.includes('Σωστά')
                                ? 'linear-gradient(to right, #059669, #10b981)'
                                : message.includes('Λάθος') || message.includes('Game Over')
                                    ? 'linear-gradient(to right, #dc2626, #ef4444)'
                                    : 'rgba(59, 130, 246, 0.3)',
                            padding: "15px",
                            borderRadius: "12px",
                            marginBottom: "20px",
                            textAlign: "center",
                            fontSize: "16px",
                            fontWeight: "500",
                            border: "1px solid rgba(255,255,255,0.3)",
                            color: "#fff"
                        }}
                    >
                        {message}
                    </motion.div>
                </AnimatePresence>}

                {/* Available Nodes */}
                <div style={{
                    display: "flex",
                    gap: "8px",
                    flexWrap: "wrap",
                    padding: "12px",
                    border: "2px solid rgba(255,255,255,0.3)",
                    borderRadius: "12px",
                    background: "rgba(255,255,255,0.05)",
                    marginBottom: "16px",
                    justifyContent: "center"
                }}>
                    {availableNodes.map((node) => (
                        <motion.div
                            key={node}
                            draggable={gameState === 'playing'}
                            onDragStart={(e) => handleDragStart(e, node)}
                            whileHover={{ scale: gameState === 'playing' ? 1.1 : 1 }}
                            whileTap={{ scale: gameState === 'playing' ? 0.95 : 1 }}
                            style={{
                                width: "48px",
                                height: "48px",
                                borderRadius: "50%",
                                backgroundColor: "#3b82f6",
                                color: "white",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontWeight: "bold",
                                cursor: gameState === 'playing' ? "grab" : "not-allowed",
                                boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                                transition: "all 0.3s ease",
                                opacity: gameState === 'playing' ? 1 : 0.5
                            }}
                        >
                            {node}
                        </motion.div>
                    ))}
                </div>

                {/* Action Buttons */}
                <div style={{
                    display: "flex",
                    justifyContent: "center",
                    gap: "12px",
                    flexWrap: "wrap"
                }}>
                    {(gameState === 'won' || gameState === 'lost') && (
                        <motion.button
                            initial={{ opacity: 0, scale: 0.8 }}
                            animate={{ opacity: 1, scale: 1 }}
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={initializeGame}
                            style={{
                                padding: "12px 24px",
                                borderRadius: "12px",
                                fontWeight: "bold",
                                background: 'linear-gradient(to right, #3b82f6, #9333ea)',
                                color: "white",
                                border: "none",
                                cursor: "pointer",
                                boxShadow: "0 5px 18px rgba(59, 130, 246, 0.4)",
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                                fontSize: "16px"
                            }}
                        >
                            <RefreshCw size={20} />
                            Νέο Παιχνίδι
                        </motion.button>
                    )}
                </div>

                {/* Score Display on Win */}
                {gameState === 'won' && (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        style={{
                            marginTop: "30px",
                            background: "linear-gradient(135deg, #059669, #10b981)",
                            padding: "25px",
                            borderRadius: "15px",
                            textAlign: "center",
                            border: "3px solid #34d399"
                        }}
                    >
                        <Trophy size={48} style={{ marginBottom: "10px" }} />
                        <h2 style={{ fontSize: "32px", margin: "10px 0" }}>
                            Score: {score}
                        </h2>
                        <p style={{ fontSize: "18px", opacity: 0.9 }}>
                            Εξαιρετική δουλειά! 🎉
                        </p>
                    </motion.div>
                )}
            </div>
        </div>
    );
};

export default HardcoreHashChallenge;